import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RotateCcw, Calculator, ChevronDown, ChevronUp, Check } from "lucide-react";
import LogToTrackerButton from "@/components/protocol/LogToTrackerButton";
import { CIWA_ITEMS, CIWA_VARIANT_TREATMENT, ciwaSeverity, ciwaSeverityForVariant } from "@/components/protocol/ciwaData";

const COLORS = {
  minimal:  { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-400", badge: "bg-emerald-600" },
  mild:     { bg: "bg-yellow-500/10",  border: "border-yellow-500/30",  text: "text-yellow-400",  badge: "bg-yellow-600" },
  moderate: { bg: "bg-orange-500/10",  border: "border-orange-500/30", text: "text-orange-400", badge: "bg-orange-600" },
  severe:   { bg: "bg-red-500/10",     border: "border-red-500/30",   text: "text-red-400",    badge: "bg-red-600" },
};

const VARIANT_LABELS = { ground: "PCP / ACP-G", air: "ACP-F", wes: "PCP-PS" };

/**
 * Inline CIWA-Ar calculator using accordion-style sections with radio dropdowns
 * for each scoring category. Matches the per-section layout the user provided
 * in screenshots (Apr 2026).
 */
export default function CIWACalculatorInline({ variant = "ground" }) {
  const [scores, setScores] = useState({});
  const [openId, setOpenId] = useState(CIWA_ITEMS[0].id);

  const total = Object.values(scores).reduce((sum, v) => sum + (v ?? 0), 0);
  const answered = Object.keys(scores).length;
  const allAnswered = answered === CIWA_ITEMS.length;
  const severityKey = ciwaSeverityForVariant(total, variant);
  const variantTable = CIWA_VARIANT_TREATMENT[variant] || CIWA_VARIANT_TREATMENT.ground;
  const severity = variantTable[severityKey];
  const C = COLORS[severityKey];

  const reset = () => { setScores({}); setOpenId(CIWA_ITEMS[0].id); };

  const setScore = (id, score) => {
    setScores((prev) => {
      const next = { ...prev, [id]: score };
      // If this completes all 10 items, collapse the box entirely
      if (Object.keys(next).length === CIWA_ITEMS.length) {
        setTimeout(() => setOpenId(null), 200);
      } else {
        // Auto-advance to next unanswered item
        const nextItem = CIWA_ITEMS.find((it) => next[it.id] === undefined);
        if (nextItem) setTimeout(() => setOpenId(nextItem.id), 200);
      }
      return next;
    });
  };

  return (
    <Card className="border-purple-500/30 bg-purple-500/5" data-testid="ciwa-calculator">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <Calculator className="w-5 h-5 text-purple-400" />
          CIWA-Ar Score
          <Badge variant="outline" className="text-[10px]">{VARIANT_LABELS[variant] || variant.toUpperCase()}</Badge>
          <Badge className="ml-auto text-base font-mono px-2 py-0.5" data-testid="ciwa-total">
            {total}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {/* Accordion list */}
        <div className="space-y-1.5" data-testid="ciwa-list">
          {CIWA_ITEMS.map((item, idx) => {
            const selected = scores[item.id];
            const isOpen = openId === item.id;
            const selectedOption = selected !== undefined ? item.options.find((o) => o.score === selected) : null;
            return (
              <div key={item.id} className={`rounded-lg border ${selected !== undefined ? "border-purple-500/40 bg-purple-500/5" : "border-border bg-secondary/10"}`}>
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : item.id)}
                  className="w-full px-3 py-2.5 flex items-center justify-between gap-2 text-left"
                  data-testid={`ciwa-section-${item.id}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-mono text-muted-foreground tabular-nums w-5">{idx + 1}.</span>
                    <span className="font-semibold text-sm truncate">{item.title}</span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    {selectedOption ? (
                      <span className="flex items-center gap-1.5">
                        <Badge className="bg-purple-600 text-white text-xs font-mono">+{selected}</Badge>
                        <Check className="w-3.5 h-3.5 text-purple-400" />
                      </span>
                    ) : (
                      <span className="text-[11px] font-medium text-purple-400">Select</span>
                    )}
                    {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                  </span>
                </button>
                {isOpen && (
                  <div className="border-t border-border bg-background/40 p-3 space-y-1.5" data-testid={`ciwa-options-${item.id}`}>
                    {item.prompt && <div className="text-[11px] italic text-muted-foreground mb-2">{item.prompt}</div>}
                    {item.options.map((opt) => (
                      <button
                        key={opt.score}
                        type="button"
                        onClick={() => setScore(item.id, opt.score)}
                        className={`w-full flex items-start gap-2 px-2.5 py-2 rounded text-left transition-colors ${selected === opt.score ? "bg-purple-600/20 ring-1 ring-purple-500/50" : "hover:bg-secondary/30"}`}
                        data-testid={`ciwa-${item.id}-${opt.score}`}
                      >
                        <span className={`mt-0.5 w-4 h-4 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors ${selected === opt.score ? "border-purple-400 bg-purple-500" : "border-muted-foreground"}`}>
                          {selected === opt.score && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                        </span>
                        <span className={`text-sm leading-snug ${selected === opt.score ? "font-medium text-purple-200" : "text-foreground"}`}>
                          {opt.label}
                          <span className="text-muted-foreground ml-1.5 font-mono text-xs">({opt.score === 0 ? "0" : `+${opt.score}`})</span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Result + treatment */}
        {answered > 0 && (
          <div className={`rounded-lg border p-3 mt-3 ${C.bg} ${C.border}`} data-testid="ciwa-result">
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Total ({answered}/{CIWA_ITEMS.length} answered)</div>
                <div className={`text-3xl font-mono font-bold ${C.text}`}>{total}</div>
              </div>
              <Badge className={`${C.badge} text-white text-sm px-2.5 py-1`}>{severity.label} ({severity.range})</Badge>
            </div>
            <div className="mt-2 pt-2 border-t border-current/10">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Treatment ({VARIANT_LABELS[variant]})</div>
              <p className={`text-sm font-semibold ${C.text}`} data-testid="ciwa-treatment">{severity.treatment}</p>
            </div>
            <div className="flex gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={reset} className="gap-1.5 h-8" data-testid="ciwa-reset">
                <RotateCcw className="w-3.5 h-3.5" />Reset
              </Button>
              <LogToTrackerButton
                className="flex-1 h-8 text-xs"
                testId="ciwa-log"
                disabled={!allAnswered}
                entry={{
                  type: "intervention",
                  name: `CIWA-Ar score: ${total}/67`,
                  dose: severity.label,
                  notes: `${VARIANT_LABELS[variant]} variant · ${severity.treatment}`,
                }}
                successLabel={`CIWA logged: ${total} (${severity.label})`}
                label={allAnswered ? "Log CIWA to Tracker" : `Answer all ${CIWA_ITEMS.length} items to log`}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
